import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ChartBarIcon,
  XMarkIcon,
  ChevronDownIcon,
  CheckIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { StatisticsConfig } from '../../pages/dashboard/StatisticsPage';

interface StatisticsSelectorProps {
  config: StatisticsConfig;
  onChange: (config: StatisticsConfig) => void;
}

interface StatisticCategory {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  items: StatisticItem[];
}

interface StatisticItem {
  key: keyof StatisticsConfig;
  name: string;
  description: string;
}

export function StatisticsSelector({ config, onChange }: StatisticsSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['basic', 'advanced', 'demographic']));
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('left');
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Calcular posición del dropdown
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const dropdownWidth = 384; // w-96 = 384px
      
      // Si el dropdown se saldría por la derecha, posicionarlo hacia la izquierda
      if (buttonRect.left + dropdownWidth > windowWidth - 20) {
        setDropdownPosition('right');
      } else {
        setDropdownPosition('left');
      }
    }
  }, [isOpen]);

  const categories: StatisticCategory[] = [
    {
      id: 'basic',
      name: 'Estadísticas Básicas',
      icon: ChartBarIcon,
      color: 'blue',
      items: [
        {
          key: 'citasPorMes',
          name: 'Citas por Mes',
          description: 'Evolución mensual de citas programadas'
        },
        {
          key: 'cancelacionesPorMes',
          name: 'Cancelaciones por Mes',
          description: 'Citas canceladas mensualmente'
        },
        {
          key: 'citasVsCancelaciones',
          name: 'Citas vs Cancelaciones',
          description: 'Comparación entre citas y cancelaciones'
        }
      ]
    },
    {
      id: 'advanced',
      name: 'Análisis Avanzado',
      icon: Squares2X2Icon,
      color: 'purple',
      items: [
        {
          key: 'topTratamientos',
          name: 'Top Tratamientos',
          description: 'Tratamientos más solicitados'
        },
        {
          key: 'rankingProfesionales',
          name: 'Ranking Profesionales',
          description: 'Profesionales con más citas'
        },
        {
          key: 'ingresosTratamiento',
          name: 'Ingresos por Tratamiento',
          description: 'Análisis de ingresos por tipo de tratamiento'
        },
        {
          key: 'horasPico',
          name: 'Horas Pico',
          description: 'Horarios con mayor demanda'
        }
      ]
    },
    {
      id: 'demographic',
      name: 'Análisis Demográfico',
      icon: ChartBarIcon,
      color: 'green',
      items: [
        {
          key: 'distribucionGenero',
          name: 'Distribución por Género',
          description: 'Pacientes distribuidos por género'
        },
        {
          key: 'distribucionEdad',
          name: 'Distribución por Edad',
          description: 'Pacientes distribuidos por rangos de edad'
        }
      ]
    }
  ];

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleStatistic = (key: keyof StatisticsConfig) => {
    onChange({
      ...config,
      [key]: !config[key]
    });
  };

  const selectAll = () => {
    const allSelected: StatisticsConfig = {};
    categories.forEach(category => {
      category.items.forEach(item => {
        allSelected[item.key] = true;
      });
    });
    onChange(allSelected);
  };

  const selectNone = () => {
    const noneSelected: StatisticsConfig = {};
    categories.forEach(category => {
      category.items.forEach(item => {
        noneSelected[item.key] = false;
      });
    });
    onChange(noneSelected);
  };

  // Presets rápidos
  const presets = [
    {
      name: '📊 Vista Básica',
      description: 'Estadísticas esenciales',
      config: {
        citasPorMes: true,
        cancelacionesPorMes: true,
        topTratamientos: true,
        rankingProfesionales: false,
        distribucionGenero: false,
        distribucionEdad: false,
        ingresosTratamiento: false,
        citasVsCancelaciones: false,
        horasPico: false
      }
    },
    {
      name: '💰 Vista Financiera',
      description: 'Enfoque en ingresos',
      config: {
        citasPorMes: true,
        cancelacionesPorMes: false,
        topTratamientos: true,
        rankingProfesionales: true,
        distribucionGenero: false,
        distribucionEdad: false,
        ingresosTratamiento: true,
        citasVsCancelaciones: false,
        horasPico: false
      }
    },
    {
      name: '👥 Vista Demográfica',
      description: 'Análisis de pacientes',
      config: {
        citasPorMes: false,
        cancelacionesPorMes: false,
        topTratamientos: true,
        rankingProfesionales: false,
        distribucionGenero: true,
        distribucionEdad: true,
        ingresosTratamiento: false,
        citasVsCancelaciones: false,
        horasPico: true
      }
    },
    {
      name: '📈 Vista Completa',
      description: 'Todas las estadísticas',
      config: {
        citasPorMes: true,
        cancelacionesPorMes: true,
        topTratamientos: true,
        rankingProfesionales: true,
        distribucionGenero: true,
        distribucionEdad: true,
        ingresosTratamiento: true,
        citasVsCancelaciones: true,
        horasPico: true
      }
    }
  ];

  const applyPreset = (presetConfig: StatisticsConfig) => {
    onChange(presetConfig);
  };

  const getSelectedCount = () => {
    return Object.values(config).filter(Boolean).length;
  };

  const getTotalCount = () => {
    return categories.reduce((total, category) => total + category.items.length, 0);
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
      purple: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
      green: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 w-full max-w-full"
      >
        <ChartBarIcon className="w-4 sm:w-5 h-4 sm:h-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
        <div className="flex-1 text-left min-w-0">
          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            Configurar Estadísticas
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {getSelectedCount()} de {getTotalCount()} seleccionadas
          </div>
        </div>
        <ChevronDownIcon 
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 z-[9999]"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className={`absolute top-full mt-2 w-full sm:w-80 lg:w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-[10000] max-h-[600px] overflow-hidden ${
            dropdownPosition === 'right' ? 'right-0' : 'left-0'
          }`}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0 flex-1 pr-2">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  Seleccionar Estadísticas
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  Elige qué gráficos mostrar en el dashboard
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Quick Actions */}
            <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex gap-2 mb-3">
                <button
                  onClick={selectAll}
                  className="flex-1 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                >
                  Seleccionar Todo
                </button>
                <button
                  onClick={selectNone}
                  className="flex-1 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Deseleccionar Todo
                </button>
              </div>
              
              {/* Presets */}
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Configuraciones Rápidas
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {presets.map((preset, index) => (
                    <button
                      key={index}
                      onClick={() => applyPreset(preset.config)}
                      className="p-2 text-left text-xs bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors"
                    >
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {preset.name}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 mt-1 truncate">
                        {preset.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="max-h-[400px] overflow-y-auto">
              {categories.map((category) => (
                <div key={category.id} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className={`p-2 rounded-lg flex-shrink-0 ${getColorClasses(category.color)}`}>
                      <category.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {category.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {category.items.filter(item => config[item.key]).length} de {category.items.length} activas
                      </div>
                    </div>
                    <ChevronDownIcon 
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
                        expandedCategories.has(category.id) ? 'rotate-180' : ''
                      }`} 
                    />
                  </button>

                  {/* Category Items */}
                  {expandedCategories.has(category.id) && (
                    <div className="px-4 pb-4">
                      {category.items.map((item) => (
                        <div
                          key={item.key}
                          className="flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-lg cursor-pointer"
                          onClick={() => toggleStatistic(item.key)}
                        >
                          <div className="relative flex items-center justify-center mt-0.5 flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={config[item.key] || false}
                              onChange={() => toggleStatistic(item.key)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-600 focus:ring-2"
                            />
                            {config[item.key] && (
                              <CheckIcon className="absolute w-3 h-3 text-blue-600 dark:text-blue-400 pointer-events-none" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                              {item.name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {item.description}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
                  {getSelectedCount()} estadísticas seleccionadas
                </span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
} 